import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  ButtonGroup,
  Pagination,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Forum, CheckCircle, HourglassEmpty, FileDownload } from '@mui/icons-material';
import { where, query, collection, getDocs, getDoc, doc, Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { usePagination } from '../hooks/usePagination';
import { Inquiry } from '../types/inquiry';
import { AuthProfile } from '../types/auth';
import { exportToCSV, CSVColumn } from '../utils/csvExporter';
import { db } from '../lib/firebase';

type FilterType = 'all' | 'pending' | 'answered';

interface LocationState {
  page?: number;
  filter?: FilterType;
}

export const ComplaintPage: React.FC = () => {
  const { adminType } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // location.state에서 저장된 페이지 정보 복원
  const locationState = location.state as LocationState | null;
  const [filter, setFilter] = useState<FilterType>(locationState?.filter || 'all');

  // CSV export 관련 상태
  const [exportStartDate, setExportStartDate] = useState<Date | null>(null);
  const [exportEndDate, setExportEndDate] = useState<Date | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // 권한별 노선 필터
  const getRouteFilter = () => {
    switch (adminType) {
      case 'route1':
        return '강서구청';
      case 'route2':
        return '하단';
      default:
        return null;
    }
  };

  const routeFilter = getRouteFilter();

  // Firestore 쿼리 제약 조건
  const getConstraints = () => {
    const constraints = [where('category', 'in', ['노선', '서비스'])];

    // 필터에 따른 조건 추가
    if (filter === 'pending') {
      constraints.push(where('isAnswered', '==', false));
    } else if (filter === 'answered') {
      constraints.push(where('isAnswered', '==', true));
    }

    // 권한별 노선 필터
    if (routeFilter) {
      constraints.push(where('route', '==', routeFilter));
    }

    return constraints;
  };

  const {
    items: inquiries,
    loading,
    currentPage,
    hasNextPage,
    hasPrevPage,
    loadFirstPage,
    loadNextPage,
    loadPrevPage,
    goToPage,
  } = usePagination<Inquiry>('inquiries', { pageSize: 10 }, ...getConstraints());

  // 초기 로드 또는 필터 변경 시
  useEffect(() => {
    if (locationState?.page && locationState?.filter === filter) {
      // 뒤로가기로 돌아온 경우 저장된 페이지로 이동
      goToPage(locationState.page);
      // state 클리어 (중복 복원 방지)
      window.history.replaceState({}, '');
    } else {
      // 필터 변경 또는 새로 진입한 경우 첫 페이지로
      loadFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleInquiryClick = (inquiryId: string) => {
    // 현재 페이지와 필터 정보를 state로 전달
    navigate(`/cs/${inquiryId}`, {
      state: {
        from: '/complaints',
        page: currentPage,
        filter: filter,
      },
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case '노선':
        return 'primary';
      case '서비스':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusChip = (isAnswered: boolean) => {
    if (isAnswered) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="처리완료"
          color="success"
          size="small"
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        icon={<HourglassEmpty />}
        label="미처리"
        color="warning"
        size="small"
        variant="outlined"
      />
    );
  };

  const handleExportCSV = async () => {
    // 날짜 검증
    if (!exportStartDate || !exportEndDate) {
      alert('시작일과 종료일을 모두 선택해주세요.');
      return;
    }

    if (exportStartDate > exportEndDate) {
      alert('시작일이 종료일보다 늦을 수 없습니다.');
      return;
    }

    setIsExporting(true);

    try {
      // 1. 날짜 범위 설정
      const startTimestamp = Timestamp.fromDate(exportStartDate);
      const endDate = new Date(exportEndDate);
      endDate.setHours(23, 59, 59, 999); // 종료일 포함
      const endTimestamp = Timestamp.fromDate(endDate);

      // 2. Firestore 쿼리 조건 구성
      const constraints = [
        where('category', 'in', ['노선', '서비스']),
        where('createdAt', '>=', startTimestamp),
        where('createdAt', '<', endTimestamp),
      ];

      // 권한별 노선 필터 추가
      if (routeFilter) {
        constraints.push(where('route', '==', routeFilter));
      }

      // 3. 데이터 조회
      const q = query(
        collection(db, 'inquiries'),
        ...constraints,
        // orderBy('createdAt', 'desc') // where와 함께 사용 시 복합 인덱스 필요
      );

      const snapshot = await getDocs(q);
      const inquiries = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Inquiry[];

      if (inquiries.length === 0) {
        alert('선택한 기간에 해당하는 민원이 없습니다.');
        return;
      }

      // 4. 답변자 UID 목록 추출 (중복 제거)
      const answererUids = Array.from(
        new Set(inquiries.filter((i) => i.answeredBy).map((i) => i.answeredBy!))
      );

      // 5. auth_profiles에서 답변자 adminType 조회
      const adminTypeMap = new Map<string, string>();
      for (const uid of answererUids) {
        const profileDoc = await getDoc(doc(db, 'auth_profiles', uid));
        if (profileDoc.exists()) {
          const profile = profileDoc.data() as AuthProfile;
          adminTypeMap.set(uid, profile.adminType);
        }
      }

      // 6. 관리자 레벨을 처리부서명으로 변환
      const getProcessingDepartment = (adminType?: string): string => {
        switch (adminType) {
          case 'super':
            return '사업관리부';
          case 'route1':
            return '운행업체(은성)';
          case 'route2':
            return '운행업체(뉴대림)';
          default:
            return '미확인';
        }
      };

      // 7. CSV 데이터 매핑
      const csvData = inquiries.map((inquiry, index) => ({
        receiptNumber: index + 1,
        complaintItem: '셔틀버스',
        complaintUser: inquiry.organization || '미지정',
        receiptDate: inquiry.createdAt,
        managementArea: inquiry.route,
        complaintContent: inquiry.content,
        processingContent: inquiry.answer,
        processingDepartment: inquiry.answeredBy
          ? getProcessingDepartment(adminTypeMap.get(inquiry.answeredBy))
          : '-',
      }));

      // 8. CSV 컬럼 정의
      const columns: CSVColumn<typeof csvData[0]>[] = [
        { key: 'receiptNumber', header: '접수번호' },
        { key: 'complaintItem', header: '민원항목' },
        { key: 'complaintUser', header: '민원인' },
        {
          key: 'receiptDate',
          header: '접수일시',
          formatter: (v) =>
            v ? format(v.toDate(), 'yyyy-MM-dd HH:mm', { locale: ko }) : '-',
        },
        { key: 'managementArea', header: '관리구역' },
        { key: 'complaintContent', header: '민원내용' },
        {
          key: 'processingContent',
          header: '처리내용',
          formatter: (v) => v || '-',
        },
        { key: 'processingDepartment', header: '처리부서' },
      ];

      // 9. CSV 파일 생성 및 다운로드
      const filename = `민원_${format(exportStartDate, 'yyyyMMdd')}_${format(
        exportEndDate,
        'yyyyMMdd'
      )}.csv`;

      exportToCSV(csvData, columns, filename);

      alert(`${inquiries.length}건의 민원이 다운로드되었습니다.`);
    } catch (error) {
      console.error('CSV export error:', error);
      alert('CSV 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Forum sx={{ fontSize: 32, color: '#667eea' }} />
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700}>
              민원 관리
            </Typography>
            <Typography variant="body2" color="text.secondary">
              사용자 민원 및 문의사항을 관리합니다.
            </Typography>
          </Box>
        </Box>

        {/* 필터 버튼 및 CSV 다운로드 */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <ButtonGroup variant="outlined">
            <Button
              variant={filter === 'all' ? 'contained' : 'outlined'}
              onClick={() => setFilter('all')}
            >
              전체
            </Button>
            <Button
              variant={filter === 'pending' ? 'contained' : 'outlined'}
              onClick={() => setFilter('pending')}
            >
              미처리
            </Button>
            <Button
              variant={filter === 'answered' ? 'contained' : 'outlined'}
              onClick={() => setFilter('answered')}
            >
              처리완료
            </Button>
          </ButtonGroup>

          {/* CSV 다운로드 영역 - super 관리자만 표시 */}
          {adminType === 'super' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                <DatePicker
                  label="시작일"
                  value={exportStartDate}
                  onChange={(newValue) => setExportStartDate(newValue)}
                  slotProps={{
                    textField: { size: 'small', sx: { width: 160 } },
                  }}
                />
                <Typography sx={{ color: 'text.secondary' }}>~</Typography>
                <DatePicker
                  label="종료일"
                  value={exportEndDate}
                  onChange={(newValue) => setExportEndDate(newValue)}
                  slotProps={{
                    textField: { size: 'small', sx: { width: 160 } },
                  }}
                />
              </LocalizationProvider>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownload />}
                onClick={handleExportCSV}
                disabled={isExporting || !exportStartDate || !exportEndDate}
                sx={{ minWidth: 120 }}
              >
                {isExporting ? 'CSV 생성 중...' : 'CSV 다운로드'}
              </Button>
            </Box>
          )}
        </Box>

        {/* 민원 목록 테이블 */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>제목</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>카테고리</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>노선</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>소속</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>작성일</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>상태</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : inquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        민원이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  inquiries.map((inquiry) => (
                    <TableRow
                      key={inquiry.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleInquiryClick(inquiry.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {inquiry.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {inquiry.content}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inquiry.category}
                          color={getCategoryColor(inquiry.category)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{inquiry.route}</TableCell>
                      <TableCell>{inquiry.organization || '미지정'}</TableCell>
                      <TableCell>
                        {inquiry.createdAt
                          ? format(inquiry.createdAt.toDate(), 'yyyy-MM-dd HH:mm', {
                              locale: ko,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell>{getStatusChip(inquiry.isAnswered)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 페이지네이션 */}
          {!loading && inquiries.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Stack spacing={2} alignItems="center">
                <Pagination
                  count={Math.max(5, hasNextPage ? currentPage + 1 : currentPage)}
                  page={currentPage}
                  onChange={(_, page) => goToPage(page)}
                  color="primary"
                  showFirstButton
                  showLastButton={false}
                  siblingCount={1}
                  boundaryCount={1}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={loadPrevPage}
                    disabled={!hasPrevPage || loading}
                  >
                    이전
                  </Button>
                  <Button
                    size="small"
                    onClick={loadNextPage}
                    disabled={!hasNextPage || loading}
                  >
                    다음
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </MainLayout>
  );
};
